import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { Box, Grid, Typography } from "@mui/material";
import { ExpirationAlert } from "Components/Shared/ExpirationAlert";
import { GridItem } from "Components/Shared/GridItem";
import { FormSection } from "Components/Shared/FormSection";
import { PartyIdentificationImagesViewer } from "Components/Shared/PartyIdentificationImages/PartyIdentificationImagesViewer";
import { AutomatedDocumentCheckSection } from "Components/Users/UserDetailPageContent/Components/PartyReadOnlyDetail/AutomatedDocumentCheckSection";
import {
  IdentityVerificationStatus,
  type PartyDto,
  PartyType,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { getDocumentExpirationStatus } from "Utils/DocumentExpiration/documentExpirationUtils";
import { formatDate } from "Utils/DateUtils";

const FORM_RESOURCES =
  Resources.Pages.Content.UserDetailPageContent.Tabs.Party.Form;
const IDENTIFICATION_RESOURCES = FORM_RESOURCES.PartyIdentification;

const EXPIRATION_RESOURCES = Resources.Components.DocumentExpirationWarning;

type Props = {
  party: PartyDto;
  currentVerificationStatus?: IdentityVerificationStatus;
};

export const DocumentsSection = ({
  party: {
    type,
    partyIdentification,
    hasIdentificationsUploaded,
    publicID,
    lastName,
  },
  currentVerificationStatus,
}: Props) => {
  const { t } = useResource();
  const legendResource =
    type === PartyType.SelfEmployed
      ? FORM_RESOURCES.SelfEmployed.Legend.Documents
      : FORM_RESOURCES.NaturalPerson.Legend.Documents;

  const isStatusExpired =
    currentVerificationStatus === IdentityVerificationStatus.Expired;

  const primaryValidTo = partyIdentification?.identificationValidTo ?? null;
  const secondaryValidTo =
    partyIdentification?.secondIdentificationValidTo ?? null;
  const primary = getDocumentExpirationStatus(primaryValidTo, isStatusExpired);
  const secondary = getDocumentExpirationStatus(secondaryValidTo);

  const hasSecondDocument = !!partyIdentification?.secondIdentification;

  return (
    <FormSection legend={t(legendResource)} noMargins>
      {primary.isExpired && (
        <ExpirationAlert
          isExpired
          title={t(EXPIRATION_RESOURCES.AdminExpiredTitle)}
          message={t(EXPIRATION_RESOURCES.AdminExpiredMessage)}
          sx={{ mb: 2 }}
        />
      )}
      {primary.isExpiringSoon && (
        <ExpirationAlert
          isExpired={false}
          title={t(EXPIRATION_RESOURCES.AdminWarningTitle)}
          message={t(EXPIRATION_RESOURCES.AdminWarningMessage, {
            date: primaryValidTo ? formatDate(primaryValidTo) : "-",
          })}
          sx={{ mb: 2 }}
        />
      )}
      {secondary.isExpired && (
        <ExpirationAlert
          isExpired
          title={t(EXPIRATION_RESOURCES.AdminExpiredSecondaryTitle)}
          message={t(EXPIRATION_RESOURCES.AdminExpiredSecondaryMessage)}
          sx={{ mb: 2 }}
        />
      )}
      {secondary.isExpiringSoon && (
        <ExpirationAlert
          isExpired={false}
          title={t(EXPIRATION_RESOURCES.AdminWarningSecondaryTitle)}
          message={t(EXPIRATION_RESOURCES.AdminWarningSecondaryMessage, {
            date: secondaryValidTo ? formatDate(secondaryValidTo) : "-",
          })}
          sx={{ mb: 2 }}
        />
      )}
      {partyIdentification && (
        <Grid container spacing={2}>
          {partyIdentification.identification && (
            <GridItem
              label={t(IDENTIFICATION_RESOURCES.Identification)}
              value={t(
                Resources.CodeLists.IdentificationList[
                  partyIdentification.identification
                ],
              )}
            />
          )}
          <GridItem
            label={t(IDENTIFICATION_RESOURCES.IdentificationNumber)}
            value={partyIdentification.identificationNumber}
          />
          <GridItem
            label={t(IDENTIFICATION_RESOURCES.IdentificationIssuer)}
            value={partyIdentification.identificationIssuer}
          />
          <GridItem
            label={t(IDENTIFICATION_RESOURCES.IdentificationValidFrom)}
            value={formatDate(partyIdentification.identificationValidFrom)}
          />
          <GridItem
            label={t(IDENTIFICATION_RESOURCES.IdentificationValidTo)}
            value={formatDate(partyIdentification.identificationValidTo)}
          />
          {partyIdentification.secondIdentificationValidTo && (
            <GridItem
              label={t(EXPIRATION_RESOURCES.SecondDocumentValidTo)}
              value={formatDate(
                partyIdentification.secondIdentificationValidTo,
              )}
            />
          )}
        </Grid>
      )}

      {hasSecondDocument && partyIdentification && (
        <>
          <Typography variant="subtitle1" fontWeight={600} mt={3} mb={1}>
            {t(IDENTIFICATION_RESOURCES.SecondDocumentTitle)}
          </Typography>
          <Grid container spacing={2}>
            <GridItem
              label={t(IDENTIFICATION_RESOURCES.Identification)}
              value={t(
                Resources.CodeLists.IdentificationList[
                  partyIdentification.secondIdentification!
                ],
              )}
            />
            <GridItem
              label={t(IDENTIFICATION_RESOURCES.IdentificationNumber)}
              value={partyIdentification.secondIdentificationNumber}
            />
            <GridItem
              label={t(IDENTIFICATION_RESOURCES.IdentificationIssuer)}
              value={partyIdentification.secondIdentificationIssuer}
            />
            <GridItem
              label={t(IDENTIFICATION_RESOURCES.IdentificationValidFrom)}
              value={formatDate(
                partyIdentification.secondIdentificationValidFrom,
              )}
            />
            <GridItem
              label={t(IDENTIFICATION_RESOURCES.IdentificationValidTo)}
              value={formatDate(
                partyIdentification.secondIdentificationValidTo,
              )}
            />
          </Grid>
        </>
      )}

      {hasIdentificationsUploaded && publicID ? (
        <>
          <Box mt={3}>
            <AutomatedDocumentCheckSection partyPublicID={publicID} />
          </Box>
          <Box mt={2}>
            <PartyIdentificationImagesViewer
              fileNamePrefix={`${t(IDENTIFICATION_RESOURCES.IdentificationsDownloadPrefix)}${lastName || ""}`}
              partyPublicID={publicID}
            />
          </Box>
        </>
      ) : (
        <Box display="inline-flex" alignItems="center" gap={0.5}>
          <WarningAmberOutlinedIcon color="warning" fontSize="small" />
          <Typography color="text.secondary">
            {t(IDENTIFICATION_RESOURCES.NoIdentificationsUploaded)}
          </Typography>
        </Box>
      )}
    </FormSection>
  );
};
